// WaveformUtils.ts
//
// Utility functions for working with passage waveforms.

import {
  IPassageWaveformSummary,
  IPassageWaveform,
  IDecodedWaveform,
  WaveformTypeLabel,
} from '@/models/PassageWaveform';

export interface WaveformOption {
  label: string;
  value: number;
  type: string;
}

/**
 * Get the list of available waveform options from a passage's passageWaveforms array.
 */
export function getAvailableWaveforms(passage: {
  passageWaveforms?: IPassageWaveformSummary[];
}): WaveformOption[] {
  if (!passage.passageWaveforms) return [];
  return passage.passageWaveforms.map((wf) => ({
    label: WaveformTypeLabel[wf.passageWaveformType] || wf.passageWaveformType,
    value: wf.passageWaveformId,
    type: wf.passageWaveformType,
  }));
}

/**
 * Get the list of waveform options that are currently verified.
 */
export function getVerifiedWaveforms(passage: {
  passageWaveforms?: IPassageWaveformSummary[];
}): WaveformOption[] {
  if (!passage.passageWaveforms) return [];
  return passage.passageWaveforms
    .filter((wf) => wf.isVerified)
    .map((wf) => ({
      label:
        WaveformTypeLabel[wf.passageWaveformType] || wf.passageWaveformType,
      value: wf.passageWaveformId,
      type: wf.passageWaveformType,
    }));
}

/**
 * Get the waveform type label for a given waveform ID.
 */
export function getWaveformLabel(
  passage: { passageWaveforms?: IPassageWaveformSummary[] },
  waveformId: number
): string {
  const wf = passage.passageWaveforms?.find(
    (w) => w.passageWaveformId === waveformId
  );
  if (wf)
    return WaveformTypeLabel[wf.passageWaveformType] || wf.passageWaveformType;
  return 'Unknown';
}

/**
 * Find a waveform by type in a passage's waveforms array.
 */
export function getWaveformByType(
  passage: { passageWaveforms?: IPassageWaveformSummary[] },
  type: string
): IPassageWaveformSummary | undefined {
  return passage.passageWaveforms?.find(
    (wf) => wf.passageWaveformType === type
  );
}

/**
 * Check if any waveform on the passage is verified.
 */
export function isAnyWaveformVerified(passage: {
  passageWaveforms?: IPassageWaveformSummary[];
}): boolean {
  return passage.passageWaveforms?.some((wf) => wf.isVerified) || false;
}

/**
 * Decodes a base64-encoded waveform string into an array of signed 16-bit
 * integers (big-endian). Each sample is 2 bytes.
 */
export function decodeBase64ToInt16Array(base64String: string): number[] {
  const binaryString = atob(base64String);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  const dataView = new DataView(bytes.buffer);
  const result: number[] = [];
  for (let i = 0; i + 1 < len; i += 2) {
    result.push(dataView.getInt16(i, false)); // big-endian
  }
  return result;
}

/**
 * Decodes the base64 `data` field on each waveform in the API response
 * and returns a cache-ready array with numeric sample data.
 */
export function decodeWaveforms(
  waveforms: Array<IPassageWaveform & { data: string }>
): IDecodedWaveform[] {
  return (waveforms || []).map((wf) => ({
    ...wf,
    data:
      wf.data && typeof wf.data === 'string'
        ? decodeBase64ToInt16Array(wf.data)
        : [],
  }));
}
